import React, { createContext, useContext } from 'react';

const PageFlowContext = createContext(false);

const VARIANT_CLASS = {
  default: 'bg-surface',
  wash: 'bg-brand-wash',
  subtle: 'bg-surface-subtle',
  muted: 'bg-surface-muted',
  gradient: 'bg-gradient-to-b from-surface-subtle via-surface to-surface-muted',
  sunken: 'bg-surface-sunken/40',
  grid: 'bg-surface-subtle bg-grid-texture',
  dots: 'bg-surface-subtle bg-dots-texture',
  elevated: 'bg-surface-muted shadow-inner-soft',
  'hero-aurora': 'bg-aurora',
  'hero-premium': 'bg-hero-premium',
  'split-migration': 'bg-split-migration',
  'split-semantic': 'bg-split-semantic',
  'platform-mesh': 'bg-platform-mesh',
  'panel-layered': 'bg-panel-layered',
  'ink-dark': 'bg-ink-900',
  'contact-accent': 'bg-contact-accent',
  'pricing-hero': 'bg-pricing-hero',
  geometric: 'bg-geometric-overlay bg-surface-subtle',
  'wave-accent': 'bg-wave-accent',
  'accent-migration':
    'bg-gradient-to-br from-brand-50/70 via-surface to-accent-50/50',
  'accent-semantic':
    'bg-gradient-to-br from-brand-50/55 via-surface to-[rgba(0,18,63,0.04)]',
  'accent-conversational':
    'bg-gradient-to-br from-brand-50/60 via-accent-50/30 to-accent-50/50',
  'accent-enterprise':
    'bg-gradient-to-br from-brand-50/80 via-surface to-accent-50/40',
  'accent-brand': 'bg-gradient-to-b from-brand-50/55 to-surface',
  divider: 'bg-surface',
  'flow-blue': 'bg-flow-blue',
  'flow-green': 'bg-flow-green',
  'flow-purple': 'bg-flow-purple',
  'flow-blue-soft': 'bg-flow-blue-soft',
  diagonal: 'bg-surface-subtle bg-diagonal-flow',
  'diagonal-green': 'bg-surface-subtle bg-diagonal-flow-green',
  rings: 'bg-surface-subtle bg-ring-accents',
  contour: 'bg-surface-subtle bg-contour-lines',
};

/** Edge handoff colors: top/bottom tones that blend into --page-canvas. */
const VARIANT_EDGE = {
  default: { top: '#f4f7fb', bottom: '#f4f7fb' },
  wash: { top: '#f8fcff', bottom: '#f4f7fb' },
  subtle: { top: '#f4f7fb', bottom: '#f4f7fb' },
  muted: { top: '#f4f7fb', bottom: '#f0f2f6' },
  gradient: { top: '#f4f7fb', bottom: '#f0f2f6' },
  grid: { top: '#f4f7fb', bottom: '#f4f7fb' },
  dots: { top: '#f4f7fb', bottom: '#f4f7fb' },
  elevated: { top: '#f0f2f6', bottom: '#f4f7fb' },
  'hero-premium': { top: '#f8fcff', bottom: '#f4f7fb' },
  'hero-aurora': { top: '#f8fcff', bottom: '#f4f7fb' },
  'split-migration': { top: '#f4f7fb', bottom: '#f0f2f6' },
  'split-semantic': { top: '#f4f7fb', bottom: '#f4f7fb' },
  'platform-mesh': { top: '#f4f7fb', bottom: '#f4f7fb' },
  'accent-migration': { top: '#f4f7fb', bottom: '#ecfeff' },
  'accent-semantic': { top: '#f4f7fb', bottom: '#eef2f7' },
  'accent-conversational': { top: '#f4f7fb', bottom: '#ecfeff' },
  'accent-enterprise': { top: '#f8fcff', bottom: '#f4f7fb' },
  'accent-brand': { top: '#f0f9ff', bottom: '#f4f7fb' },
  'flow-blue': { top: '#f0f9ff', bottom: '#f4f7fb' },
  'flow-green': { top: '#eff9ff', bottom: '#f4f7fb' },
  'flow-purple': { top: '#ecfeff', bottom: '#f4f7fb' },
  'flow-blue-soft': { top: '#f4f7fb', bottom: '#f0f9ff' },
  diagonal: { top: '#f4f7fb', bottom: '#f4f7fb' },
  'diagonal-green': { top: '#f4f7fb', bottom: '#f4f7fb' },
  rings: { top: '#f4f7fb', bottom: '#f4f7fb' },
  contour: { top: '#f4f7fb', bottom: '#f4f7fb' },
  geometric: { top: '#f4f7fb', bottom: '#f4f7fb' },
  'wave-accent': { top: '#f4f7fb', bottom: '#f0f2f6' },
  'contact-accent': { top: '#f8fcff', bottom: '#f0f9ff' },
  'pricing-hero': { top: '#f8fcff', bottom: '#f0f9ff' },
  'panel-layered': { top: '#f0f2f6', bottom: '#f0f2f6' },
  'ink-dark': { top: '#f4f7fb', bottom: '#f0f2f6' },
  sunken: { top: '#f4f7fb', bottom: '#f4f7fb' },
  divider: { top: '#f4f7fb', bottom: '#f4f7fb' },
};

const VARIANT_CONNECT = {
  default: 'neutral',
  wash: 'brand',
  subtle: 'neutral',
  muted: 'neutral',
  gradient: 'neutral',
  grid: 'neutral',
  dots: 'neutral',
  elevated: 'neutral',
  'hero-premium': 'brand',
  'hero-aurora': 'brand',
  'split-migration': 'brand',
  'split-semantic': 'brand',
  'platform-mesh': 'brand',
  'accent-migration': 'brand',
  'accent-semantic': 'brand',
  'accent-conversational': 'brand',
  'accent-enterprise': 'brand',
  'accent-brand': 'brand',
  'flow-blue': 'brand',
  'flow-green': 'brand',
  'flow-purple': 'brand',
  'flow-blue-soft': 'brand',
  diagonal: 'brand',
  'diagonal-green': 'brand',
  rings: 'brand',
  contour: 'brand',
  geometric: 'brand',
  'wave-accent': 'neutral',
  'contact-accent': 'brand',
  'pricing-hero': 'brand',
  'panel-layered': 'neutral',
  'ink-dark': 'brand',
};

const DECOR_CLASS = {
  flow: 'bg-flow-lines opacity-50',
  'flow-strong': 'bg-flow-lines opacity-70',
  mesh: 'bg-platform-mesh-lines opacity-40',
  wave: 'bg-wave-soft',
  nodes: 'bg-node-lines opacity-60',
  contour: 'bg-contour-lines opacity-70',
  diagonal: 'bg-diagonal-flow opacity-80',
  'diagonal-green': 'bg-diagonal-flow-green opacity-80',
  rings: 'bg-ring-accents opacity-80',
  circuit: 'bg-circuit-lines opacity-70',
};

const CONNECT_CLASS = {
  brand: 'section-connect-brand',
  blue: 'section-connect-blue',
  green: 'section-connect-green',
  purple: 'section-connect-purple',
  neutral: 'section-connect-neutral',
};

/**
 * Page wrapper: continuous canvas; stacked SectionSurface bands use transparent fills.
 */
export function PageFlow({ className = '', children, ...rest }) {
  return (
    <PageFlowContext.Provider value={true}>
      <main id="main-content" className={`page-flow ${className}`.trim()} {...rest}>
        {children}
      </main>
    </PageFlowContext.Provider>
  );
}

/**
 * Consistent section backgrounds: alternating tones without loud color blocks.
 *
 * @param {string} variant: base background (see VARIANT_CLASS)
 * @param {string} decor: optional overlay texture (see DECOR_CLASS) or 'none'/'blobs'
 * @param {'none'|'top'|'bottom'|'both'} bleed: widen edge fade zone (canvas handoff)
 * @param {'brand'|'blue'|'green'|'purple'|'neutral'} connectTo: accent wash at seam
 * @param {boolean} flowTop: top edge handoff gradient into the section above
 * @param {boolean} flowBottom: seam marker for overlap chain (no bottom fade overlay; canvas only)
 * @param {boolean} overlap: negative margin seam so backgrounds interleave
 * @param {boolean} underNav: pull background under transparent sticky navbar
 * @param {string} bgAccent: optional extra background class on the decor layer (e.g. hero lightning)
 * @param {React.ReactNode} accentEl: optional decorative element in the background layer
 */
export default function SectionSurface({
  variant = 'default',
  decor = 'none',
  bleed = 'none',
  connectTo,
  flowTop = false,
  flowBottom = false,
  overlap = false,
  underNav = false,
  bgAccent = '',
  accentEl = null,
  as: Tag = 'div',
  className = '',
  bordered = false,
  children,
  ...rest
}) {
  const inPageFlow = useContext(PageFlowContext);
  const base = VARIANT_CLASS[variant] ?? VARIANT_CLASS.default;
  const tone = connectTo ?? VARIANT_CONNECT[variant] ?? 'neutral';
  const edge = VARIANT_EDGE[variant] ?? VARIANT_EDGE.default;

  const border = bordered ? 'border-y border-ink-200/40' : '';

  const decorClass = decor === 'blobs' ? 'section-decor-blobs' : DECOR_CLASS[decor] || '';

  const bleedFade =
    bleed === 'both' || bleed === 'top' || bleed === 'bottom' ? '6rem' : '5rem';

  const edgeStyle = inPageFlow
    ? {
        '--section-bg-top': edge.top,
        '--section-bg-bottom': edge.bottom,
        '--section-edge-fade': bleedFade,
      }
    : undefined;

  const flowClass = [
    CONNECT_CLASS[tone] || CONNECT_CLASS.neutral,
    inPageFlow ? 'section-surface--canvas' : '',
    flowTop ? 'section-handoff-top' : '',
    flowBottom ? 'section-seam-next' : '',
    overlap ? 'section-overlap' : '',
    underNav ? 'section-under-nav' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const showHandoffTop = inPageFlow && flowTop;

  return (
    <Tag
      className={`section-surface relative isolate ${flowClass} ${border} ${className}`.trim()}
      data-variant={variant}
      style={edgeStyle}
      {...rest}
    >
      <div aria-hidden="true" className="section-surface-bg pointer-events-none absolute inset-0 z-0">
        <div className={`section-surface-fill absolute inset-0 ${base}`} />

        {decor !== 'none' && (
          <div
            className={`section-surface-decor absolute inset-0 overflow-x-clip overflow-y-visible ${decorClass}`}
          />
        )}

        {bgAccent ? <div className={`section-surface-accent absolute inset-0 ${bgAccent}`} /> : null}

        {accentEl ? (
          <div className="section-surface-accent-el pointer-events-none absolute inset-0 overflow-hidden">
            {accentEl}
          </div>
        ) : null}

        {showHandoffTop && <div className="section-surface-handoff-top" aria-hidden="true" />}
      </div>

      <div className="section-surface-content relative z-20">{children}</div>
    </Tag>
  );
}

/** Gradient hairline between major sections (optional; prefer canvas handoff) */
export function SectionDivider() {
  return <div className="divider-gradient w-full" aria-hidden="true" />;
}
